import React, { useMemo } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { colors } from "utils";
import { useAuthReady } from "hooks/useAuthReady";
import { request } from "hooks/useOkoApi/useOkoApi";
import { Transcript } from "../SessionSummaries/Transcript";

const NO_CHALLENGES = {};

const formatTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" });
};

/**
 * Merges the lines fetched on open with those streamed since, into the phase
 * shape the report's Transcript renders. Lines can arrive twice (fetch and
 * stream) and out of order (different server pods), so dedupe on event_id and
 * sort by time before grouping consecutive lines by question.
 */
export const toPhases = (fetched = [], streamed = []) => {
  const byId = new Map();
  for (const line of [...fetched, ...streamed]) byId.set(line.event_id, line);
  const lines = [...byId.values()].sort(
    (a, b) => new Date(a.occurred_at) - new Date(b.occurred_at),
  );

  const phases = [];
  for (const line of lines) {
    const label =
      line.question_order != null ? `Question ${line.question_order}` : "Before the first question";
    let phase = phases[phases.length - 1];
    if (!phase || phase.label !== label) {
      phase = { label, events: [] };
      phases.push(phase);
    }
    const timestamp = formatTime(line.occurred_at);
    phase.events.push(
      line.speaker === "tutor"
        ? { type: "bot_message", content: line.text, timestamp }
        : { type: "speech", speaker: line.student_name || "Student", content: line.text, timestamp },
    );
  }
  return phases;
};

// The transcript so far for a running session. Fetched once on open (and again
// after a reconnect, via snapshotCount) for what was said before; new lines
// then arrive on the live stream, so there is nothing to refresh by hand.
export const LiveTranscriptTab = ({ sessionId, streamedLines, snapshotCount, highlightQuote }) => {
  const authReady = useAuthReady();
  const { data, isInitialLoading, isError } = useQuery({
    queryKey: ["live-transcript", sessionId, snapshotCount],
    queryFn: () => request(`live/sessions/${sessionId}/transcript`, { method: "GET" }),
    enabled: Boolean(sessionId) && authReady,
    staleTime: Infinity,
    // A reconnect changes the key; keep the earlier lines on screen meanwhile.
    keepPreviousData: true,
  });

  const phases = useMemo(() => toPhases(data?.lines, streamedLines), [data, streamedLines]);

  let body;
  if (phases.length > 0) {
    body = <Transcript transcript={phases} challengeData={NO_CHALLENGES} highlightQuote={highlightQuote} />;
  } else if (isInitialLoading) {
    body = <Muted>Loading transcript…</Muted>;
  } else if (!isError) {
    body = <Muted>No one has spoken yet. Lines appear here as students talk.</Muted>;
  }

  return (
    <div>
      <Note>
        This is a rough live transcript. The full transcript will be in the session report after the
        session ends.
      </Note>
      {isError && !data && (
        <Muted role="alert">
          Earlier lines couldn't be loaded. New lines still appear as students talk.
        </Muted>
      )}
      {body}
    </div>
  );
};

const Note = styled.p`
  margin: 0 0 8px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-style: italic;
  color: ${colors.nightlyWoods500};
`;

const Muted = styled.p`
  margin: 16px 0 0;
  font-family: Inter, sans-serif;
  font-size: 16px;
  color: ${colors.nightlyWoods500};
`;
